import { trpc } from '../lib/trpc.js';
import { paraApi, useFiltro } from '../estado/filtros.js';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { Tile } from '../componentes/Tile.js';
import { Barras, type Barra } from '../componentes/Barras.js';
import { competencia as fmtCompetencia, inteiro, moeda, moedaOuTraco, participacao } from '../lib/formato.js';

export function Resumo() {
  const { filtro } = useFiltro();
  const consulta = trpc.resumo.totais.useQuery({ filtro: paraApi(filtro) });

  if (consulta.isPending) {
    return (
      <section className="panel">
        <Carregando linhas={6} rotulo="Carregando o resumo" />
      </section>
    );
  }
  if (consulta.error) {
    return (
      <section className="panel">
        <Erro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      </section>
    );
  }

  const { geral: t, documentosComCno, porTipo, porCompetencia } = consulta.data;

  if (t.documentos === 0) {
    return (
      <section className="panel">
        <Vazio titulo="Nenhum documento nesta seleção">
          Envie XMLs na área acima, ou afrouxe os filtros aplicados na aba Documentos.
        </Vazio>
      </section>
    );
  }

  const series: Barra[] = [
    { nome: 'ICMS', valor: t.icms },
    { nome: 'ICMS-ST', valor: t.icmsSt },
    { nome: 'IPI', valor: t.ipi },
    { nome: 'PIS', valor: t.pis },
    { nome: 'COFINS', valor: t.cofins },
    { nome: 'ISS', valor: t.iss },
    { nome: 'IBS estadual', valor: t.ibsUF, rtc: true },
    { nome: 'IBS municipal', valor: t.ibsMun, rtc: true },
    { nome: 'CBS', valor: t.cbs, rtc: true },
  ];

  return (
    <section className="panel">
      <div className="tiles">
        <Tile
          hero
          contagem
          rotulo="Documentos lidos"
          valor={t.documentos}
          sub={`${inteiro(documentosComCno)} com CNO`}
        />
        <Tile
          hero
          rotulo="Valor dos documentos"
          valor={t.valorTotal}
          sub={`produtos ${moeda(t.valorProdutos)} · serviços ${moeda(t.valorServicos)}`}
        />
        <Tile
          hero
          rotulo="Tributos destacados"
          valor={t.totalTributos}
          sub={`${participacao(t.totalTributos, t.valorTotal)} do valor`}
        />
        <Tile
          hero
          rotulo="Retenções"
          valor={t.totalRetencoes}
          sub={`ISS ${moeda(t.retIss)} · INSS ${moeda(t.retInss)}`}
        />
      </div>

      <div className="eyebrow">
        <span className="fam" />
        Sistema atual
      </div>
      <div className="tiles">
        <Tile rotulo="ICMS próprio" valor={t.icms} sub={`base ${moeda(t.bcIcms)}`} />
        <Tile rotulo="ICMS-ST" valor={t.icmsSt} />
        <Tile rotulo="IPI" valor={t.ipi} />
        <Tile rotulo="PIS" valor={t.pis} />
        <Tile rotulo="COFINS" valor={t.cofins} />
        <Tile rotulo="ISS destacado" valor={t.iss} sub={`base ${moeda(t.bcIss)}`} />
      </div>

      <div className="eyebrow">
        <span className="fam rtc" />
        Transição da reforma (IBS e CBS)
      </div>
      <div className="tiles">
        <Tile
          familia="rtc"
          rotulo="Base IBS/CBS"
          valor={t.bcRtc}
          sub={`${inteiro(t.comGrupoRtc)} de ${inteiro(t.documentos)} documentos com o grupo`}
        />
        <Tile familia="rtc" rotulo="IBS estadual" valor={t.ibsUF} sub={participacao(t.ibsUF, t.bcRtc)} />
        <Tile familia="rtc" rotulo="IBS municipal" valor={t.ibsMun} sub={participacao(t.ibsMun, t.bcRtc)} />
        <Tile familia="rtc" rotulo="CBS" valor={t.cbs} sub={participacao(t.cbs, t.bcRtc)} />
      </div>

      <div className="eyebrow">Retenções na fonte</div>
      <div className="tiles">
        <Tile
          familia="ret"
          rotulo="ISS retido"
          valor={t.retIss}
          sub={`${inteiro(t.comIssRetido)} nota(s) com retenção`}
        />
        <Tile familia="ret" rotulo="INSS (previdenciária)" valor={t.retInss} />
        <Tile familia="ret" rotulo="IRRF" valor={t.retIrrf} />
        <Tile familia="ret" rotulo="CSLL" valor={t.retCsll} />
        <Tile familia="ret" rotulo="PIS retido" valor={t.retPis} />
        <Tile familia="ret" rotulo="COFINS retido" valor={t.retCofins} />
        {t.retOutras > 0.005 ? <Tile familia="ret" rotulo="Outras retenções" valor={t.retOutras} /> : null}
      </div>

      {series.some((s) => s.valor > 0.005) ? (
        <>
          <div className="eyebrow">Composição do tributo destacado</div>
          <Barras series={series} />
        </>
      ) : null}

      <div className="eyebrow">Por tipo de documento</div>
      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>Tipo</th>
              <th className="r">Docs</th>
              <th className="r">Valor</th>
              <th className="r">ICMS</th>
              <th className="r">PIS</th>
              <th className="r">COFINS</th>
              <th className="r">ISS</th>
              <th className="r">IBS</th>
              <th className="r">CBS</th>
              <th className="r">ISS ret.</th>
              <th className="r">INSS ret.</th>
            </tr>
          </thead>
          <tbody>
            {porTipo.map((linha) => (
              <tr key={linha.tipo}>
                <td className="strong">{linha.tipo}</td>
                <td className="num r">{inteiro(linha.documentos)}</td>
                <td className="num r">{moedaOuTraco(linha.valorTotal)}</td>
                <td className="num r">{moedaOuTraco(linha.icms)}</td>
                <td className="num r">{moedaOuTraco(linha.pis)}</td>
                <td className="num r">{moedaOuTraco(linha.cofins)}</td>
                <td className="num r">{moedaOuTraco(linha.iss)}</td>
                <td className="num r">{moedaOuTraco(linha.ibs)}</td>
                <td className="num r">{moedaOuTraco(linha.cbs)}</td>
                <td className="num r">{moedaOuTraco(linha.retIss)}</td>
                <td className="num r">{moedaOuTraco(linha.retInss)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {porCompetencia.length > 1 ? (
        <>
          <div className="eyebrow">Por competência</div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Competência</th>
                  <th className="r">Docs</th>
                  <th className="r">Valor</th>
                  <th className="r">Tributos</th>
                  <th className="r">Retenções</th>
                </tr>
              </thead>
              <tbody>
                {porCompetencia.map((linha) => (
                  <tr key={linha.competencia}>
                    <td className="strong mono">{fmtCompetencia(linha.competencia)}</td>
                    <td className="num r">{inteiro(linha.documentos)}</td>
                    <td className="num r">{moeda(linha.valorTotal)}</td>
                    <td className="num r">{moeda(linha.totalTributos)}</td>
                    <td className="num r">{moedaOuTraco(linha.totalRetencoes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
